import { useState, useEffect, useCallback } from "react"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import type { User } from "@/lib/supabase/types"
import styles from "./users-page.module.css"
import { useToast } from "@/client/components/common/toast"
import { useAdminStore } from "@/shared/hooks/use-admin-store"
import {
  adminAdjustBalance,
  getUserBbpBalance,
  updateUserVerified,
  createAdminLog,
  adminCreateUser,
  adminDeleteUser,
  adminUpdateUserEmail,
  adminFreezeUser,
  adminUnfreezeUser,
  adminResetPassword,
  updateUserProfile,
  type CreateUserInput,
  type UpdateProfileInput,
} from "@/repositories/admin.repository"

type UserLevel = 1 | 2 | 3 | 4 | 5
type DisplayStatus = "active" | "frozen"

interface AdminUser extends Pick<User, "id" | "nickname" | "phone" | "email" | "avatar_url" | "level" | "points" | "role" | "created_at" | "last_login_at"> {
  isFrozen: boolean
  isVerified: boolean
}

interface UserDetail extends AdminUser {
  tasksCompleted: number
  coursesEnrolled: number
  school: string | null
  company: string | null
  bbpBalance: number
  direction: string | null
  bio: string | null
}

interface UserStats {
  total: number
  active: number
  frozen: number
  newThisMonth: number
}

const PAGE_SIZE = 15

const LEVEL_NAMES: Record<number, string> = {
  1: "L1 青铜",
  2: "L2 白银",
  3: "L3 黄金",
  4: "L4 铂金",
  5: "L5 钻石",
}

const STATUS_MAP: Record<DisplayStatus, string> = {
  active: "活跃",
  frozen: "已冻结",
}

const ROLE_OPTIONS = [
  { key: "engineer", label: "工程师" },
  { key: "enterprise", label: "企业" },
  { key: "admin", label: "管理员" },
  { key: "super_admin", label: "超级管理员" },
]

function toLevel(n: number): UserLevel {
  return Math.min(Math.max(Math.round(n), 1), 5) as UserLevel
}

function getLevelClass(level: number): string {
  const l = toLevel(level)
  return styles[`levelL${l}`] ?? ""
}

function getAvatarClass(level: number): string {
  const l = toLevel(level)
  return styles[`avatarL${l}`] ?? ""
}

function parseRoleTags(role: string | null | undefined): string[] {
  return (role ?? "").split(",").filter(Boolean)
}

async function sendUserNotification(
  supabase: ReturnType<typeof getBrowserSupabaseClient>,
  userId: string,
  type: string,
  title: string,
  content: string,
) {
  if (!supabase) return
  try { await supabase.from("notifications").insert({ user_id: userId, type, title, content }) } catch {}
}

export default function UsersPage() {
  const toast = useToast()
  const { admin } = useAdminStore()
  const isSuperAdmin = admin?.role === "super_admin"

  const [users, setUsers] = useState<AdminUser[]>([])
  const [stats, setStats] = useState<UserStats>({ total: 0, active: 0, frozen: 0, newThisMonth: 0 })
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<DisplayStatus | "all">("all")
  const [levelFilter, setLevelFilter] = useState<UserLevel | "all">("all")
  const [sortField, setSortField] = useState<string>("created_at")
  const [sortAsc, setSortAsc] = useState(false)

  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [showLevelModal, setShowLevelModal] = useState(false)
  const [targetLevel, setTargetLevel] = useState<UserLevel>(3)
  const [updating, setUpdating] = useState(false)

  // BBP 调整弹窗
  const [showBbpModal, setShowBbpModal] = useState(false)
  const [bbpAmount, setBbpAmount] = useState("")
  const [bbpNote, setBbpNote] = useState("")

  // 创建用户弹窗
  const [showAddModal, setShowAddModal] = useState(false)
  const [addForm, setAddForm] = useState({ email: "", password: "", nickname: "", phone: "", school: "", company: "", direction: "", bio: "", level: "1", role: "user" })

  // 编辑用户弹窗
  const [showEditModal, setShowEditModal] = useState(false)
  const [editForm, setEditForm] = useState({ email: "", originalEmail: "", nickname: "", phone: "", school: "", company: "", direction: "", bio: "" })

  // 删除确认弹窗
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")

  // 操作原因弹窗
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [reasonAction, setReasonAction] = useState<"freeze" | "unfreeze" | "adjustLevel" | "removeRole">("freeze")
  const [reasonText, setReasonText] = useState("")
  const [pendingActionData, setPendingActionData] = useState<Record<string, unknown> | null>(null)

  // 密码重置弹窗
  const [showResetPwdModal, setShowResetPwdModal] = useState(false)
  const [newPassword, setNewPassword] = useState("")

  // 批量操作
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const allVisibleSelected = users.length > 0 && users.every((u) => selectedIds.has(u.id))

  const fetchUsers = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) { setLoading(false); return }

    setLoading(true)

    try {
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()

      const countRes = await supabase.from("users").select("id, role, is_verified, created_at", { count: "exact", head: false })
      const allRows = countRes.data ?? []
      const total = countRes.count ?? allRows.length
      const frozen = allRows.filter((u) => parseRoleTags(u.role).includes("frozen")).length
      const newThisMonth = allRows.filter((u) => u.created_at >= monthStart).length

      setStats({ total, active: total - frozen, frozen, newThisMonth })

      let query = supabase
        .from("users")
        .select("id, nickname, phone, email, avatar_url, level, points, role, is_verified, created_at, last_login_at", { count: "exact" })
        .order(sortField, { ascending: sortAsc })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      // #11: 搜索扩展 — nickname / phone / email
      if (searchQuery.trim()) {
        const term = searchQuery.trim()
        query = query.or(`nickname.ilike.%${term}%,phone.ilike.%${term}%,email.ilike.%${term}%`)
      }

      // 等级筛选
      if (levelFilter !== "all") {
        query = query.eq("level", levelFilter)
      }

      // #10: 状态筛选移到后端查询
      if (statusFilter === "frozen") {
        query = query.like("role", "%frozen%")
      } else if (statusFilter === "active") {
        query = query.not("role", "like", "%frozen%")
      }

      const { data, count } = await query

      const rows: AdminUser[] = (data ?? []).map((u) => ({
        ...u,
        isFrozen: parseRoleTags(u.role).includes("frozen"),
        isVerified: (u as Record<string, unknown>).is_verified as boolean ?? false,
      }))

      setUsers(rows)
      setTotalCount(count ?? rows.length)
    } catch {
      toast.error("加载用户列表失败")
    } finally {
      setLoading(false)
    }
  }, [page, searchQuery, statusFilter, levelFilter, sortField, sortAsc, toast])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const fetchUserDetail = useCallback(async (userId: string) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setDetailLoading(true)

    try {
      const { data: profile } = await supabase.from("users").select("*").eq("id", userId).single()
      if (!profile) { setDetailLoading(false); return }

      const [reputationRes, enrollmentsRes, balance] = await Promise.all([
        supabase.from("reputation").select("tasks_completed").eq("user_id", userId).single(),
        supabase.from("enrollments").select("id", { count: "exact", head: true }).eq("user_id", userId),
        getUserBbpBalance(supabase, userId),
      ])

      const detail: UserDetail = {
        id: profile.id,
        nickname: profile.nickname,
        phone: profile.phone,
        email: profile.email,
        avatar_url: profile.avatar_url,
        level: profile.level,
        points: profile.points,
        role: profile.role,
        created_at: profile.created_at,
        last_login_at: profile.last_login_at ?? null,
        isFrozen: parseRoleTags(profile.role).includes("frozen"),
        isVerified: profile.is_verified ?? false,
        school: profile.school,
        company: profile.company,
        direction: profile.direction ?? null,
        bio: profile.bio ?? null,
        tasksCompleted: reputationRes.data?.tasks_completed ?? 0,
        coursesEnrolled: enrollmentsRes.count ?? 0,
        bbpBalance: balance,
      }

      setSelectedUser(detail)
    } catch {
      toast.error("加载用户详情失败")
    } finally {
      setDetailLoading(false)
    }
  }, [toast])

  const handleToggleFreeze = useCallback(async (user: AdminUser, reason?: string) => {
    if (updating) return
    const supabase = getBrowserSupabaseClient()

    // P0 #2: 自操作防护 — 不能冻结自己
    if (!user.isFrozen && user.id === admin?.id) {
      toast.error("不能冻结自己的账号")
      return
    }

    // 冻结操作需要填写原因
    if (!user.isFrozen && !reason) {
      setReasonAction("freeze")
      setReasonText("")
      setPendingActionData({ userId: user.id })
      setShowReasonModal(true)
      return
    }

    setUpdating(true)

    try {
      if (user.isFrozen) {
        await adminUnfreezeUser(user.id, reason)
      } else {
        await adminFreezeUser(user.id, reason)
      }

      const currentTags = parseRoleTags(user.role)
      const newTags = user.isFrozen
        ? currentTags.filter((t) => t !== "frozen")
        : [...currentTags, "frozen"]

      toast.success(user.isFrozen ? `已解冻用户 ${user.nickname}` : `已冻结用户 ${user.nickname}`)

      sendUserNotification(supabase, user.id, user.isFrozen ? "account_unfrozen" : "account_frozen",
        user.isFrozen ? "账号已解冻" : "账号已被冻结",
        user.isFrozen ? "您的账号已被管理员解冻，现在可以正常使用。" : `您的账号已被管理员冻结${reason ? `，原因：${reason}` : ""}。如有疑问请联系客服。`,
      )

      if (selectedUser?.id === user.id) {
        setSelectedUser((prev) => prev ? { ...prev, isFrozen: !user.isFrozen, role: newTags.join(",") } : null)
      }
      await fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "操作失败，请重试")
    } finally {
      setUpdating(false)
    }
  }, [updating, selectedUser, fetchUsers, admin, toast])

  const handleAdjustLevel = useCallback(async (user: AdminUser, newLevel: UserLevel) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    // P0 #2: 降级自己时阻止
    if (user.id === admin?.id && newLevel < toLevel(user.level)) {
      toast.error("不能降低自己的等级")
      return
    }

    setUpdating(true)

    try {
      const { error } = await supabase.from("users").update({ level: newLevel }).eq("id", user.id)
      if (error) throw error

      if (admin?.id) {
        createAdminLog(supabase, {
          adminId: admin.id,
          action: "adjust_level",
          targetType: "user",
          targetId: user.id,
          details: `将用户 ${user.nickname} 等级从 L${toLevel(user.level)} 调整为 L${newLevel}`,
        }).catch(() => {})
      }

      toast.success(`已将 ${user.nickname} 等级调整为 L${newLevel}`)

      sendUserNotification(supabase, user.id, "level_change", "等级调整通知",
        `您的等级已从 L${toLevel(user.level)} 调整为 L${newLevel}。`,
      )
      setShowLevelModal(false)
      if (selectedUser?.id === user.id) {
        setSelectedUser((prev) => prev ? { ...prev, level: newLevel } : null)
      }
      await fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "调整等级失败")
    } finally {
      setUpdating(false)
    }
  }, [selectedUser, fetchUsers, admin, toast])

  const handleAdjustBbp = useCallback(async () => {
    if (!selectedUser) return
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    const amount = Number(bbpAmount)
    if (!amount || isNaN(amount)) {
      toast.error("请输入有效金额")
      return
    }
    if (!bbpNote.trim()) {
      toast.error("请填写调整备注")
      return
    }

    setUpdating(true)

    try {
      await adminAdjustBalance(supabase, selectedUser.id, amount, bbpNote)

      if (admin?.id) {
        createAdminLog(supabase, {
          adminId: admin.id,
          action: "adjust_bbp",
          targetType: "user",
          targetId: selectedUser.id,
          details: `调整 ${selectedUser.nickname} BBP ${amount > 0 ? "+" : ""}${amount}，备注：${bbpNote}`,
        }).catch(() => {})
      }

      toast.success(`已调整 ${selectedUser.nickname} BBP ${amount > 0 ? "+" : ""}${amount}`)

      sendUserNotification(supabase, selectedUser.id, "balance_change", "余额变动通知",
        `您的 BBP 余额已${amount > 0 ? "增加" : "减少"} ${Math.abs(amount)}，备注：${bbpNote || "无"}。`,
      )

      const newBalance = await getUserBbpBalance(supabase, selectedUser.id)
      setSelectedUser((prev) => prev ? { ...prev, bbpBalance: newBalance } : null)

      await fetchUsers()

      setShowBbpModal(false)
      setBbpAmount("")
      setBbpNote("")
    } catch (err) {
      const msg = err instanceof Error ? err.message : "调整失败"
      toast.error(msg)
    } finally {
      setUpdating(false)
    }
  }, [selectedUser, bbpAmount, bbpNote, admin, toast, fetchUsers])

  const handleCreateUser = useCallback(async () => {
    if (!addForm.email || !addForm.password || !addForm.nickname) {
      toast.error("请填写必填字段：邮箱、密码、昵称")
      return
    }

    setUpdating(true)
    try {
      const input: CreateUserInput = {
        email: addForm.email,
        password: addForm.password,
        nickname: addForm.nickname,
        phone: addForm.phone || undefined,
        school: addForm.school || undefined,
        company: addForm.company || undefined,
        direction: addForm.direction || undefined,
        bio: addForm.bio || undefined,
        level: addForm.level ? Number(addForm.level) : undefined,
        role: addForm.role || undefined,
      }

      await adminCreateUser(input)
      toast.success(`用户 ${addForm.nickname} 创建成功`)
      setShowAddModal(false)
      setAddForm({ email: "", password: "", nickname: "", phone: "", school: "", company: "", direction: "", bio: "", level: "1", role: "user" })
      await fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "创建用户失败")
    } finally {
      setUpdating(false)
    }
  }, [addForm, toast, fetchUsers])

  const handleEditUser = useCallback(async () => {
    if (!selectedUser) return
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setUpdating(true)
    try {
      // 邮箱变更需要通过 Edge Function 更新 Auth
      const emailChanged = editForm.email !== editForm.originalEmail
      if (emailChanged && editForm.email) {
        await adminUpdateUserEmail(selectedUser.id, editForm.email)
      }

      const input: UpdateProfileInput = {}
      if (editForm.nickname) input.nickname = editForm.nickname
      if (editForm.phone) input.phone = editForm.phone
      if (editForm.school) input.school = editForm.school
      if (editForm.company) input.company = editForm.company
      if (editForm.direction) input.direction = editForm.direction
      if (editForm.bio) input.bio = editForm.bio

      await updateUserProfile(supabase, selectedUser.id, input)

      if (admin?.id) {
        createAdminLog(supabase, {
          adminId: admin.id,
          action: "edit_user_profile",
          targetType: "user",
          targetId: selectedUser.id,
          details: `编辑用户 ${selectedUser.nickname} 的资料${emailChanged ? "（含邮箱变更）" : ""}`,
        }).catch(() => {})
      }

      toast.success("用户资料更新成功")
      setShowEditModal(false)
      await fetchUserDetail(selectedUser.id)
      await fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "更新失败")
    } finally {
      setUpdating(false)
    }
  }, [selectedUser, editForm, admin, toast, fetchUsers, fetchUserDetail])

  const handleDeleteUser = useCallback(async () => {
    if (!selectedUser) return

    setUpdating(true)
    try {
      await adminDeleteUser(selectedUser.id)
      toast.success(`已删除用户 ${selectedUser.nickname}`)
      setShowDeleteConfirm(false)
      setSelectedUser(null)
      await fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "删除用户失败")
    } finally {
      setUpdating(false)
    }
  }, [selectedUser, toast, fetchUsers])

  const handleToggleVerified = useCallback(async (user: AdminUser) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setUpdating(true)

    try {
      const newVerified = !user.isVerified
      await updateUserVerified(supabase, user.id, newVerified)

      if (admin?.id) {
        createAdminLog(supabase, {
          adminId: admin.id,
          action: newVerified ? "verify_user" : "unverify_user",
          targetType: "user",
          targetId: user.id,
          details: `${newVerified ? "认证" : "取消认证"}用户 ${user.nickname}`,
        }).catch(() => {})
      }

      toast.success(newVerified ? `已认证 ${user.nickname}` : `已取消 ${user.nickname} 认证`)
      if (selectedUser?.id === user.id) {
        setSelectedUser((prev) => prev ? { ...prev, isVerified: newVerified } : null)
      }
      await fetchUsers()
    } catch {
      toast.error("认证操作失败")
    } finally {
      setUpdating(false)
    }
  }, [selectedUser, fetchUsers, admin, toast])

  const handleToggleRole = useCallback(async (user: AdminUser, roleKey: string) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    // P0 #2: 不能移除自己的 admin/super_admin 角色
    const tags = parseRoleTags(user.role)
    if (user.id === admin?.id && tags.includes(roleKey) && (roleKey === "admin" || roleKey === "super_admin")) {
      toast.error("不能移除自己的管理员角色")
      return
    }

    // P2 #20: 唯一管理员保护 — 移除 admin/super_admin 前检查是否最后一个
    if (tags.includes(roleKey) && (roleKey === "admin" || roleKey === "super_admin")) {
      const { count } = await supabase
        .from("users")
        .select("id", { count: "exact", head: true })
        .like("role", `%${roleKey}%`)
      if (count !== null && count <= 1) {
        toast.error(`不能移除：该用户是最后一个 ${roleKey === "super_admin" ? "超级管理员" : "管理员"}`)
        return
      }
    }

    setUpdating(true)

    try {
      const tags = parseRoleTags(user.role)
      let newTags: string[]
      let actionLabel: string

      if (tags.includes(roleKey)) {
        newTags = tags.filter((t) => t !== roleKey)
        actionLabel = "移除"
      } else {
        newTags = [...tags, roleKey]
        actionLabel = "添加"
      }

      const roleStr = newTags.join(",")
      const { error } = await supabase.from("users").update({ role: roleStr }).eq("id", user.id)
      if (error) throw error

      if (admin?.id) {
        createAdminLog(supabase, {
          adminId: admin.id,
          action: "update_role",
          targetType: "user",
          targetId: user.id,
          details: `${actionLabel}用户 ${user.nickname} 的 ${roleKey} 角色`,
        }).catch(() => {})
      }

      toast.success(`已${actionLabel} ${user.nickname} 的 ${roleKey} 角色`)

      sendUserNotification(supabase, user.id, "role_change", "角色变更通知",
        `您已被${actionLabel}「${ROLE_OPTIONS.find((o) => o.key === roleKey)?.label ?? roleKey}」角色。`,
      )
      if (selectedUser?.id === user.id) {
        setSelectedUser((prev) => prev ? { ...prev, role: roleStr, isFrozen: newTags.includes("frozen") } : null)
      }
      await fetchUsers()
    } catch {
      toast.error("角色操作失败")
    } finally {
      setUpdating(false)
    }
  }, [selectedUser, fetchUsers, admin, toast])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>用户管理</h1>
        <div className={styles.actions}>
          <input
            type="text"
            placeholder="搜索用户名、手机号或邮箱..."
            className={styles.searchInput}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0) }}
          />
          <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
            + 添加用户
          </button>
          <button className={styles.refreshBtn} onClick={() => fetchUsers()} disabled={loading}>
            {loading ? "加载中..." : "刷新"}
          </button>
          <button className={styles.exportBtn} onClick={() => {
            const header = "ID,昵称,邮箱,手机,等级,积分,认证,状态,注册时间,最近登录\n"
            const rows = users.map((u) =>
              [u.id, u.nickname, u.email ?? "", u.phone ?? "", `L${toLevel(u.level)}`, u.points, u.isVerified ? "已认证" : "未认证", u.isFrozen ? "已冻结" : "活跃", new Date(u.created_at).toLocaleDateString("zh-CN"), u.last_login_at ? new Date(u.last_login_at).toLocaleString("zh-CN") : ""].join(",")
            ).join("\n")
            const blob = new Blob(["\uFEFF" + header + rows], { type: "text/csv;charset=utf-8" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`
            a.click()
            URL.revokeObjectURL(url)
          }}>
            导出 CSV
          </button>
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>👥</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.total.toLocaleString()}</span>
            <span className={styles.statLabel}>总用户</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.statActive}`}>
          <div className={styles.statIcon}>✓</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.active.toLocaleString()}</span>
            <span className={styles.statLabel}>活跃用户</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.statFrozen}`}>
          <div className={styles.statIcon}>❄️</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.frozen}</span>
            <span className={styles.statLabel}>冻结用户</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.statNew}`}>
          <div className={styles.statIcon}>🆕</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.newThisMonth}</span>
            <span className={styles.statLabel}>本月新增</span>
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <select className={styles.filterSelect} value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as DisplayStatus | "all"); setPage(0) }}>
          <option value="all">全部状态</option>
          <option value="active">活跃</option>
          <option value="frozen">已冻结</option>
        </select>
        <select className={styles.filterSelect} value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value === "all" ? "all" : Number(e.target.value) as UserLevel); setPage(0) }}>
          <option value="all">全部等级</option>
          <option value="1">L1 青铜</option>
          <option value="2">L2 白银</option>
          <option value="3">L3 黄金</option>
          <option value="4">L4 铂金</option>
          <option value="5">L5 钻石</option>
        </select>
        <span className={styles.filterCount}>共 {totalCount} 个用户</span>
      </div>

      <div className={styles.table}>
        {selectedIds.size > 0 && (
          <div className={styles.batchBar}>
            <span>已选择 {selectedIds.size} 个用户</span>
            <button className={styles.batchBtn} onClick={async () => {
              for (const uid of selectedIds) {
                const u = users.find((x) => x.id === uid)
                if (u && !u.isFrozen) await handleToggleFreeze(u)
              }
              setSelectedIds(new Set())
            }} disabled={updating}>批量冻结</button>
            <button className={styles.batchBtn} onClick={async () => {
              for (const uid of selectedIds) {
                const u = users.find((x) => x.id === uid)
                if (u && u.isFrozen) await handleToggleFreeze(u)
              }
              setSelectedIds(new Set())
            }} disabled={updating}>批量解冻</button>
            <button className={styles.batchCancelBtn} onClick={() => setSelectedIds(new Set())}>取消选择</button>
          </div>
        )}
        <div className={styles.tableHeader}>
          <input type="checkbox" checked={allVisibleSelected} onChange={() => {
            if (allVisibleSelected) { setSelectedIds(new Set()) } else { setSelectedIds(new Set(users.map((u) => u.id))) }
          }} className={styles.rowCheckbox} />
          <span>用户</span>
          <span className={styles.sortable} onClick={() => { setSortField("level"); setSortAsc(sortField === "level" ? !sortAsc : false) }}>
            等级 {sortField === "level" ? (sortAsc ? "↑" : "↓") : ""}
          </span>
          <span className={styles.sortable} onClick={() => { setSortField("points"); setSortAsc(sortField === "points" ? !sortAsc : false) }}>
            积分 {sortField === "points" ? (sortAsc ? "↑" : "↓") : ""}
          </span>
          <span>认证</span>
          <span>状态</span>
          <span className={styles.sortable} onClick={() => { setSortField("last_login_at"); setSortAsc(sortField === "last_login_at" ? !sortAsc : true) }}>
            最近登录 {sortField === "last_login_at" ? (sortAsc ? "↑" : "↓") : ""}
          </span>
          <span className={styles.sortable} onClick={() => { setSortField("created_at"); setSortAsc(sortField === "created_at" ? !sortAsc : false) }}>
            注册时间 {sortField === "created_at" ? (sortAsc ? "↑" : "↓") : ""}
          </span>
          <span>操作</span>
        </div>
        {users.length === 0 ? (
          <div className={styles.empty}>{loading ? "加载中..." : "未找到匹配的用户"}</div>
        ) : (
          users.map((user) => (
            <div key={user.id} className={styles.tableRow}>
              <input type="checkbox" checked={selectedIds.has(user.id)} onChange={() => {
                setSelectedIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(user.id)) { next.delete(user.id) } else { next.add(user.id) }
                  return next
                })
              }} className={styles.rowCheckbox} />
              <div className={styles.userCell}>
                <div className={`${styles.avatar} ${getAvatarClass(user.level)}`}>
                  {user.nickname[0]}
                </div>
                <div>
                  <span className={styles.userName}>{user.nickname}</span>
                  <span className={styles.userPhone}>{user.phone ?? "未绑定"}</span>
                </div>
              </div>
              <span className={`${styles.level} ${getLevelClass(user.level)}`}>
                L{toLevel(user.level)}
              </span>
              <span className={styles.points}>{user.points.toLocaleString()}</span>
              <span>
                {user.isVerified ? (
                  <span className={styles.verifiedBadge}>已认证</span>
                ) : (
                  <span className={styles.unverifiedBadge}>未认证</span>
                )}
              </span>
              <span className={`${styles.status} ${user.isFrozen ? styles.frozen : styles.active}`}>
                {user.isFrozen ? "已冻结" : "活跃"}
              </span>
              <span className={styles.lastLogin}>
                {user.last_login_at
                  ? new Date(user.last_login_at).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
                  : "—"}
              </span>
              <span className={styles.date}>
                {new Date(user.created_at).toLocaleDateString("zh-CN")}
              </span>
              <div className={styles.actions}>
                <button className={styles.viewBtn} onClick={() => fetchUserDetail(user.id)}>
                  详情
                </button>
                <button
                  className={`${styles.freezeBtn} ${user.isFrozen ? styles.unfreeze : ""}`}
                  onClick={() => handleToggleFreeze(user)}
                  disabled={updating}
                >
                  {user.isFrozen ? "解冻" : "冻结"}
                </button>
                <button className={styles.levelBtn} onClick={() => {
                  setSelectedUser({ ...user, tasksCompleted: 0, coursesEnrolled: 0, school: null, company: null, bbpBalance: 0, direction: null, bio: null })
                  setTargetLevel(toLevel(user.level))
                  setShowLevelModal(true)
                }}>
                  调级
                </button>
                {isSuperAdmin && (
                  <button className={styles.deleteBtn} onClick={() => {
                    setSelectedUser({ ...user, tasksCompleted: 0, coursesEnrolled: 0, school: null, company: null, bbpBalance: 0, direction: null, bio: null })
                    setShowDeleteConfirm(true)
                  }}>
                    删除
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.pagination}>
        <button className={styles.pageBtn} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
          <span className={`${styles.arrow} ${styles.prev}`}>←</span>
          上一页
        </button>
        <span className={styles.pageInfo}>{page + 1} / {totalPages}</span>
        <button className={styles.pageBtn} disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
          下一页
          <span className={`${styles.arrow} ${styles.next}`}>→</span>
        </button>
      </div>

      {/* 用户详情弹窗 */}
      {selectedUser && !showLevelModal && !showBbpModal && (
        <div className={styles.modalOverlay} onClick={() => setSelectedUser(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>用户详情</h2>
              <button className={styles.closeBtn} onClick={() => setSelectedUser(null)}>×</button>
            </div>
            <div className={styles.modalBody}>
              {detailLoading ? (
                <div style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>加载中...</div>
              ) : (
                <>
                  <div className={styles.userProfile}>
                    <div className={`${styles.profileAvatar} ${getAvatarClass(selectedUser.level)}`}>
                      {selectedUser.nickname[0]}
                    </div>
                    <div className={styles.profileInfo}>
                      <h3>{selectedUser.nickname}</h3>
                      <div className={styles.profileBadges}>
                        <span className={`${styles.levelBadge} ${getLevelClass(selectedUser.level)}`}>
                          L{toLevel(selectedUser.level)} · {LEVEL_NAMES[toLevel(selectedUser.level)] ?? ""}
                        </span>
                        {selectedUser.isVerified && <span className={styles.verifiedBadgeSmall}>✓ 已认证</span>}
                      </div>
                    </div>
                  </div>

                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>📱</div>
                      <div className={styles.detailContent}>
                        <label>手机号</label>
                        <span>{selectedUser.phone ?? "未绑定"}</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>✉️</div>
                      <div className={styles.detailContent}>
                        <label>邮箱</label>
                        <span>{selectedUser.email ?? "未绑定"}</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>⭐</div>
                      <div className={styles.detailContent}>
                        <label>积分</label>
                        <span>{selectedUser.points.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>💎</div>
                      <div className={styles.detailContent}>
                        <label>BBP 余额</label>
                        <span className={styles.bbpBalance}>
                          {selectedUser.bbpBalance.toLocaleString()}
                          <button className={styles.bbpAdjustBtn} onClick={() => { setShowBbpModal(true) }}>
                            调整
                          </button>
                        </span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>{selectedUser.isFrozen ? "❄️" : "✓"}</div>
                      <div className={styles.detailContent}>
                        <label>状态</label>
                        <span className={`${styles.status} ${selectedUser.isFrozen ? styles.frozen : styles.active}`}>
                          {STATUS_MAP[selectedUser.isFrozen ? "frozen" : "active"]}
                        </span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>{selectedUser.isVerified ? "🔐" : "🔓"}</div>
                      <div className={styles.detailContent}>
                        <label>认证</label>
                        <button
                          className={`${styles.verifiedToggle} ${selectedUser.isVerified ? styles.verifiedOn : styles.verifiedOff}`}
                          onClick={() => handleToggleVerified(selectedUser)}
                          disabled={updating}
                        >
                          {selectedUser.isVerified ? "已认证 (点击取消)" : "未认证 (点击认证)"}
                        </button>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>🎓</div>
                      <div className={styles.detailContent}>
                        <label>学校</label>
                        <span>{selectedUser.school ?? "未填写"}</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>🏢</div>
                      <div className={styles.detailContent}>
                        <label>公司</label>
                        <span>{selectedUser.company ?? "未填写"}</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>📅</div>
                      <div className={styles.detailContent}>
                        <label>注册时间</label>
                        <span>{new Date(selectedUser.created_at).toLocaleDateString("zh-CN")}</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>📋</div>
                      <div className={styles.detailContent}>
                        <label>完成任务</label>
                        <span>{selectedUser.tasksCompleted} 个</span>
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailIcon}>📚</div>
                      <div className={styles.detailContent}>
                        <label>报名课程</label>
                        <span>{selectedUser.coursesEnrolled} 门</span>
                      </div>
                    </div>
                  </div>

                  {/* 角色管理 */}
                  <div className={styles.roleSection}>
                    <label className={styles.roleLabel}>角色管理</label>
                    <div className={styles.roleGrid}>
                      {ROLE_OPTIONS.map((opt) => {
                        const active = parseRoleTags(selectedUser.role).includes(opt.key)
                        return (
                          <button
                            key={opt.key}
                            className={`${styles.roleChip} ${active ? styles.roleActive : styles.roleInactive}`}
                            onClick={() => handleToggleRole(selectedUser, opt.key)}
                            disabled={updating}
                          >
                            {active ? "✓ " : ""}{opt.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <div className={styles.modalFooterLeft}>
                {isSuperAdmin && (
                  <button className={styles.deleteBtn} disabled={updating}
                    onClick={() => setShowDeleteConfirm(true)}>
                    🗑️ 删除用户
                  </button>
                )}
              </div>
              <div className={styles.modalFooterRight}>
                <button className={styles.freezeBtn} disabled={updating}
                  onClick={() => handleToggleFreeze(selectedUser)}>
                  {selectedUser.isFrozen ? "❄️ 解冻用户" : "🔒 冻结用户"}
                </button>
                <button className={styles.levelBtn} onClick={() => {
                  setTargetLevel(toLevel(selectedUser.level))
                  setShowLevelModal(true)
                }}>
                  ⬆️ 调整等级
                </button>
                <button className={styles.levelBtn} onClick={() => {
                  setNewPassword("")
                  setShowResetPwdModal(true)
                }}>
                  🔑 重置密码
                </button>
                <button className={styles.editBtn} onClick={() => {
                  setEditForm({
                    email: selectedUser.email ?? "",
                    originalEmail: selectedUser.email ?? "",
                    nickname: selectedUser.nickname ?? "",
                    phone: selectedUser.phone ?? "",
                    school: selectedUser.school ?? "",
                    company: selectedUser.company ?? "",
                    direction: selectedUser.direction ?? "",
                    bio: selectedUser.bio ?? "",
                  })
                  setShowEditModal(true)
                }}>
                  ✏️ 编辑资料
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 调级弹窗 */}
      {showLevelModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowLevelModal(false)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>调整用户等级</h2>
              <button className={styles.closeBtn} onClick={() => setShowLevelModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.levelHint}>
                当前: <strong>L{toLevel(selectedUser.level)} · {LEVEL_NAMES[toLevel(selectedUser.level)]}</strong>
              </p>
              <div className={styles.levelSelect}>
                {([1, 2, 3, 4, 5] as UserLevel[]).map((level) => (
                  <button
                    key={level}
                    className={`${styles.levelOption} ${targetLevel === level ? styles.selected : ""} ${styles[`levelL${level}`] ?? ""}`}
                    onClick={() => setTargetLevel(level)}
                  >
                    L{level}
                    <span className={styles.levelName}>{LEVEL_NAMES[level]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowLevelModal(false)}>
                取消
              </button>
              <button className={styles.confirmBtn}
                onClick={() => handleAdjustLevel(selectedUser, targetLevel)}
                disabled={targetLevel === toLevel(selectedUser.level) || updating}>
                {updating ? "更新中..." : "确认调整"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BBP 调整弹窗 */}
      {showBbpModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowBbpModal(false)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>调整 BBP 余额</h2>
              <button className={styles.closeBtn} onClick={() => setShowBbpModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.bbpInfo}>
                <span className={styles.bbpCurrentLabel}>当前余额</span>
                <span className={styles.bbpCurrentValue}>{selectedUser.bbpBalance.toLocaleString()} BBP</span>
              </div>
              <div className={styles.formGroup}>
                <label>调整金额</label>
                <div className={styles.bbpInputRow}>
                  <button
                    className={styles.bbpSignBtn}
                    onClick={() => {
                      const v = Number(bbpAmount)
                      if (v > 0) setBbpAmount(String(-v))
                      else if (v < 0) setBbpAmount(String(Math.abs(v)))
                    }}
                  >
                    {Number(bbpAmount) >= 0 ? "＋" : "－"}
                  </button>
                  <input
                    type="number"
                    className={styles.bbpInput}
                    value={bbpAmount}
                    onChange={(e) => setBbpAmount(e.target.value)}
                    placeholder="输入金额（正数充值，负数扣减）"
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>调整备注</label>
                <textarea
                  className={styles.bbpTextarea}
                  value={bbpNote}
                  onChange={(e) => setBbpNote(e.target.value)}
                  placeholder="请输入调整原因（必填）"
                  rows={3}
                />
              </div>
              {bbpAmount && !isNaN(Number(bbpAmount)) && Number(bbpAmount) !== 0 && (
                <div className={styles.bbpPreview}>
                  调整后余额：<strong>{(selectedUser.bbpBalance + Number(bbpAmount)).toLocaleString()} BBP</strong>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowBbpModal(false); setBbpAmount(""); setBbpNote("") }}>
                取消
              </button>
              <button className={styles.confirmBtn}
                onClick={handleAdjustBbp}
                disabled={!bbpAmount || !bbpNote.trim() || updating}>
                {updating ? "处理中..." : "确认调整"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加用户弹窗 */}
      {showAddModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>添加用户</h2>
              <button className={styles.closeBtn} onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>邮箱 *</label>
                  <input type="email" className={styles.formInput} value={addForm.email}
                    onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="user@example.com" />
                </div>
                <div className={styles.formGroup}>
                  <label>密码 *</label>
                  <input type="text" className={styles.formInput} value={addForm.password}
                    onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="至少6位" />
                </div>
                <div className={styles.formGroup}>
                  <label>昵称 *</label>
                  <input type="text" className={styles.formInput} value={addForm.nickname}
                    onChange={(e) => setAddForm((f) => ({ ...f, nickname: e.target.value }))}
                    placeholder="用户昵称" />
                </div>
                <div className={styles.formGroup}>
                  <label>手机号</label>
                  <input type="text" className={styles.formInput} value={addForm.phone}
                    onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="选填" />
                </div>
                <div className={styles.formGroup}>
                  <label>学校</label>
                  <input type="text" className={styles.formInput} value={addForm.school}
                    onChange={(e) => setAddForm((f) => ({ ...f, school: e.target.value }))}
                    placeholder="选填" />
                </div>
                <div className={styles.formGroup}>
                  <label>公司</label>
                  <input type="text" className={styles.formInput} value={addForm.company}
                    onChange={(e) => setAddForm((f) => ({ ...f, company: e.target.value }))}
                    placeholder="选填" />
                </div>
                <div className={styles.formGroup}>
                  <label>方向</label>
                  <input type="text" className={styles.formInput} value={addForm.direction}
                    onChange={(e) => setAddForm((f) => ({ ...f, direction: e.target.value }))}
                    placeholder="选填" />
                </div>
                <div className={styles.formGroup}>
                  <label>等级</label>
                  <select className={styles.formInput} value={addForm.level}
                    onChange={(e) => setAddForm((f) => ({ ...f, level: e.target.value }))}>
                    <option value="1">L1 青铜</option>
                    <option value="2">L2 白银</option>
                    <option value="3">L3 黄金</option>
                    <option value="4">L4 铂金</option>
                    <option value="5">L5 钻石</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>角色</label>
                  <select className={styles.formInput} value={addForm.role}
                    onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}>
                    <option value="user">普通用户</option>
                    <option value="engineer">工程师</option>
                    <option value="enterprise">企业</option>
                    <option value="admin">管理员</option>
                    <option value="super_admin">超级管理员</option>
                  </select>
                </div>
              </div>
              <div className={styles.formGroup} style={{ marginTop: 12 }}>
                <label>简介</label>
                <textarea className={styles.formTextarea} value={addForm.bio}
                  onChange={(e) => setAddForm((f) => ({ ...f, bio: e.target.value }))}
                  placeholder="选填" rows={3} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => {
                setShowAddModal(false)
                setAddForm({ email: "", password: "", nickname: "", phone: "", school: "", company: "", direction: "", bio: "", level: "1", role: "user" })
              }}>
                取消
              </button>
              <button className={styles.confirmBtn} onClick={handleCreateUser}
                disabled={updating || !addForm.email || !addForm.password || !addForm.nickname}>
                {updating ? "创建中..." : "创建用户"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑用户弹窗 */}
      {showEditModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>编辑用户资料 — {selectedUser.nickname}</h2>
              <button className={styles.closeBtn} onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>邮箱</label>
                  <input type="email" className={styles.formInput} value={editForm.email}
                    onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
                  {editForm.email !== editForm.originalEmail && (
                    <span style={{ color: "#f59e0b", fontSize: 12, marginTop: 4 }}>邮箱变更将通过 Auth 系统更新</span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label>昵称</label>
                  <input type="text" className={styles.formInput} value={editForm.nickname}
                    onChange={(e) => setEditForm((f) => ({ ...f, nickname: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label>手机号</label>
                  <input type="text" className={styles.formInput} value={editForm.phone}
                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label>学校</label>
                  <input type="text" className={styles.formInput} value={editForm.school}
                    onChange={(e) => setEditForm((f) => ({ ...f, school: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label>公司</label>
                  <input type="text" className={styles.formInput} value={editForm.company}
                    onChange={(e) => setEditForm((f) => ({ ...f, company: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label>方向</label>
                  <input type="text" className={styles.formInput} value={editForm.direction}
                    onChange={(e) => setEditForm((f) => ({ ...f, direction: e.target.value }))} />
                </div>
              </div>
              <div className={styles.formGroup} style={{ marginTop: 12 }}>
                <label>简介</label>
                <textarea className={styles.formTextarea} value={editForm.bio}
                  onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))}
                  rows={3} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowEditModal(false)}>
                取消
              </button>
              <button className={styles.confirmBtn} onClick={handleEditUser} disabled={updating}>
                {updating ? "保存中..." : "保存修改"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认弹窗 */}
      {showDeleteConfirm && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>确认删除用户</h2>
              <button className={styles.closeBtn} onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText("") }}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.deleteWarning}>
                <p>确定要删除用户 <strong>{selectedUser.nickname}</strong>（{selectedUser.email}）吗？</p>
                <p className={styles.deleteHint}>此操作将标记删除该用户的账户，为安全起见请输入 <strong>DELETE</strong> 确认：</p>
                <input
                  type="text"
                  className={styles.resetPwdInput}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder='输入 "DELETE" 确认删除'
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText("") }}>
                取消
              </button>
              <button className={styles.dangerBtn} onClick={handleDeleteUser} disabled={updating || deleteConfirmText !== "DELETE"}>
                {updating ? "删除中..." : "确认删除"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P0 #4: 操作原因弹窗 */}
      {showReasonModal && (
        <div className={styles.modalOverlay} onClick={() => setShowReasonModal(false)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{reasonAction === "freeze" ? "冻结用户" : reasonAction === "unfreeze" ? "解冻用户" : "操作确认"}</h2>
              <button className={styles.closeBtn} onClick={() => setShowReasonModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <label className={styles.fieldLabel}>操作原因 *</label>
              <textarea
                className={styles.reasonTextarea}
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="请输入操作原因..."
                rows={3}
              />
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowReasonModal(false); setPendingActionData(null) }}>
                取消
              </button>
              <button
                className={styles.confirmBtn}
                disabled={!reasonText.trim() || updating}
                onClick={async () => {
                  if (!reasonText.trim()) return
                  setShowReasonModal(false)
                  const userId = pendingActionData?.userId as string | undefined
                  if (!userId) return
                  const user = users.find((u) => u.id === userId)
                  if (!user) return
                  if (reasonAction === "freeze") {
                    await handleToggleFreeze(user, reasonText.trim())
                  }
                  setPendingActionData(null)
                  setReasonText("")
                }}
              >
                {updating ? "处理中..." : "确认"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P0 #6: 密码重置弹窗 */}
      {showResetPwdModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowResetPwdModal(false)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>重置用户密码</h2>
              <button className={styles.closeBtn} onClick={() => setShowResetPwdModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <p>为用户 <strong>{selectedUser.nickname}</strong> 设置新密码：</p>
              <input
                type="password"
                className={styles.input}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="输入新密码（至少 6 位）"
                minLength={6}
              />
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowResetPwdModal(false); setNewPassword("") }}>
                取消
              </button>
              <button
                className={styles.dangerBtn}
                disabled={!newPassword || newPassword.length < 6 || updating}
                onClick={async () => {
                  setUpdating(true)
                  try {
                    await adminResetPassword(selectedUser.id, newPassword)
                    toast.success(`已重置 ${selectedUser.nickname} 的密码`)
                    setShowResetPwdModal(false)
                    setNewPassword("")
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "重置密码失败")
                  } finally {
                    setUpdating(false)
                  }
                }}
              >
                {updating ? "重置中..." : "确认重置"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
